import { Select, SelectProps } from '@dixa/lib-shared-habla';
import { SearchUserHit } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback } from 'react';

import { searchApi } from 'apis/search';
import { PhoneNumberCountryFlag } from 'components/phoneNumberCountryFlag';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import { PhoneNumberUserSelectItem } from './phoneNumberUserSelect.types';
import { searchUserToPhoneNumberSelectItem } from './utils/userToSelectItem';

export type PhoneNumberUserSelectValue = {
  phoneNumber?: string;
  searchUserHit?: SearchUserHit;
};

export type PhoneNumberUserSelectProps = {
  /** The default selected phone number. */
  phoneNumber?: string;
  fixedCountryCode?: string;
  /** Whether to use infinite scroll instead of manual pagination. */
  infiniteScroll?: boolean;
  showRegionFlagPrefix?: boolean;
  /** The callback for a phone number is selected. */
  onSelect: (value: PhoneNumberUserSelectValue) => void;
} & Pick<SelectProps<PhoneNumberUserSelectItem>, 'onToggle' | 'status'> &
  PropsWithClassName;

/** Represents the component for selecting a phone number. */
export const PhoneNumberUserSelect = ({
  className,
  phoneNumber,
  infiniteScroll = false,
  showRegionFlagPrefix,
  onSelect,
  onToggle,
  status,
}: PhoneNumberUserSelectProps): JSX.Element => {
  const {
    arg,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items: users,
    nextPage,
    previousPage,
    search,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    initialArg: {
      sort: {
        key: 'display_name',
      },
      filter: {
        facets: {
          has_phone_number: [true],
        },
      },
    },
    itemsSelector: ({ data }) => data.results.hits,
    infinite: infiniteScroll,
  });

  /** Handles selecting a phone number. */
  const handleSelect = useCallback(
    (item: PhoneNumberUserSelectItem) => {
      const searchUserHit = users.find(
        ({ phone_number }) => phone_number === item.id,
      );

      onSelect({
        searchUserHit,
        phoneNumber: searchUserHit?.phone_number ?? item.name,
      });
    },
    [onSelect, users],
  );

  /** Handles toggling the dropdown of the select component. */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        search(arg?.searchTerm ?? '', { debounceTime: 0 });
      }
      onToggle?.(isOpen);
    },
    [arg?.searchTerm, onToggle, search],
  );

  /** Handles navigating to the next page. */
  const handleNextPage = useCallback((): Promise<void> => {
    return Promise.resolve(nextPage());
  }, [nextPage]);

  /** Handles navigating to the previous page. */
  const handlePreviousPage = useCallback((): Promise<void> => {
    return Promise.resolve(previousPage());
  }, [previousPage]);

  return (
    <Select
      status={status}
      prefix={
        showRegionFlagPrefix
          ? {
              type: 'custom',
              value: <PhoneNumberCountryFlag phoneNumber={phoneNumber} />,
              noWrap: false,
            }
          : undefined
      }
      className={className}
      emptyMessage={
        arg?.searchTerm
          ? 'No phone numbers found'
          : 'No phone numbers available'
      }
      items={
        // the search term is added as a select item,
        // but it empty search is not an option.
        arg?.searchTerm && arg.searchTerm != ''
          ? [
              {
                id: arg.searchTerm,
                name: arg.searchTerm,
              },

              ...users.map(searchUserToPhoneNumberSelectItem),
            ]
          : [...users.map(searchUserToPhoneNumberSelectItem)]
      }
      loading={asyncStatus === 'pending'}
      onSearch={(value) => search(value, { debounceTime: 300 })}
      onSelect={handleSelect}
      onToggle={handleToggle}
      pagination={{
        infinite: infiniteScroll,
        nextEnable: hasNextPage,
        previousEnable: hasPreviousPage,
        onNext: handleNextPage,
        onPrevious: handlePreviousPage,
      }}
      placeholder="Type a phone number or user name"
      searchable
      selectedItems={phoneNumber ? [phoneNumber] : []}
    />
  );
};
